import api from "./client";

// Billing mutations target the CURRENTLY VIEWED workspace's billing account.
// Read its id from the same localStorage the app persists on workspace switch.
// Without sending client_id, the backend falls back to the user's DEFAULT
// workspace — so subscribing / topping up while viewing another workspace would
// silently hit the wrong billing account.
const currentClientId = () => {
  try {
    return JSON.parse(localStorage.getItem("currentWorkspace") || "null")?.id;
  } catch {
    return undefined;
  }
};

// Billing API
export const billingAPI = {
  // Get billing summary for the current workspace. clientId is optional —
  // backend falls back to the user's default workspace when omitted — but
  // passing it explicitly avoids a race when the user just switched.
  getBillingSummary: async (clientId) => {
    const response = await api.get("/api/v1/billing/summary", {
      params: clientId ? { client_id: clientId } : {},
    });
    return response.data.data;
  },

  // Get transactions for the currently viewed workspace. Defaults client_id to
  // the active workspace (like the mutations) so the ledger doesn't silently
  // show the DEFAULT workspace's transactions; an explicit params.client_id
  // (threaded from the page) wins.
  getTransactions: async (params = {}) => {
    const response = await api.get("/api/v1/billing/transactions", {
      params: { client_id: currentClientId(), ...params },
    });
    return response.data;
  },

  // Get current subscription for the current workspace.
  getCurrentSubscription: async (clientId) => {
    const response = await api.get("/api/v1/subscription/current", {
      params: clientId ? { client_id: clientId } : {},
    });
    return response.data.data;
  },

  // Start subscription checkout
  startSubscribeCheckout: async (planId) => {
    const response = await api.post("/api/v1/subscription/checkout", {
      plan_id: planId,
      client_id: currentClientId(),
    });
    return response.data.data;
  },

  // Upgrade subscription to a different plan
  upgradePlan: async (planId) => {
    const response = await api.post("/api/v1/subscription/upgrade", {
      plan_id: planId,
      client_id: currentClientId(),
    });
    return response.data.data;
  },

  // Downgrade subscription to a different plan
  downgradePlan: async (planId) => {
    const response = await api.post("/api/v1/subscription/downgrade", {
      plan_id: planId,
      client_id: currentClientId(),
    });
    return response.data.data;
  },

  // Cancel current subscription
  cancelSubscription: async () => {
    const response = await api.post("/api/v1/subscription/cancel", {
      client_id: currentClientId(),
    });
    return response.data.data;
  },

  // Reactivate cancelled subscription
  reactivateSubscription: async () => {
    const response = await api.post("/api/v1/subscription/reactivate", {
      client_id: currentClientId(),
    });
    return response.data.data;
  },

  // Get available top-up packs
  listTopUpPacks: async () => {
    const response = await api.get("/api/v1/top_ups");
    return response.data.data;
  },

  // Start top-up checkout
  startTopUpCheckout: async (packId) => {
    const response = await api.post("/api/v1/top_ups/checkout", {
      top_up_pack_id: packId,
      client_id: currentClientId(),
    });
    return response.data.data;
  },

  // Pay-as-you-go top-up: buy an arbitrary number of credits ($1 = 25 credits).
  startCustomTopUpCheckout: async (credits) => {
    const response = await api.post("/api/v1/top_ups/custom_checkout", {
      credits,
      client_id: currentClientId(),
    });
    return response.data.data;
  },

  // Live price quote for a credit quantity (no Stripe call).
  quoteTopUp: async (credits) => {
    const response = await api.get("/api/v1/top_ups/quote", {
      params: { credits },
    });
    return response.data.data;
  },

  // Open billing portal for the currently viewed workspace's billing account.
  openBillingPortal: async () => {
    const response = await api.post("/api/v1/billing/portal", {
      client_id: currentClientId(),
    });
    return response.data.data;
  },

  // Get available billing plans
  listPlans: async () => {
    const response = await api.get("/api/v1/plans");
    return response.data.data;
  },

  // List usage alerts for current user
  listUsageAlerts: async () => {
    const response = await api.get("/api/v1/credit_usage_alerts");
    return response.data.data;
  },

  // Create a new usage alert
  createUsageAlert: async (attrs) => {
    const response = await api.post("/api/v1/credit_usage_alerts", attrs);
    return response.data.data;
  },

  // Update an existing usage alert
  updateUsageAlert: async (id, attrs) => {
    const response = await api.patch(`/api/v1/credit_usage_alerts/${id}`, attrs);
    return response.data.data;
  },

  // Delete a usage alert
  deleteUsageAlert: async (id) => {
    await api.delete(`/api/v1/credit_usage_alerts/${id}`);
  },
};
